import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Plus, Minus, FileDown, LogOut, X, FileSpreadsheet, Search } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { BranchComparison } from "../components/BranchComparison";
import { BranchCompareChart } from "../components/BranchCompareChart";
import { AccountDialog } from "../components/AccountDialog";
import { exportEntriesExcel } from "../lib/excel";
import { SitesSidebar } from "../components/SitesSidebar";
import { NameDialog } from "../components/NameDialog";
import { EntryDialog } from "../components/EntryDialog";
import { SummaryCards } from "../components/SummaryCards";
import { EntriesTable } from "../components/EntriesTable";
import { Filters } from "../components/Filters";
import { Overview } from "../components/Overview";
import { StaffManager } from "../components/StaffManager";
import { ActivityLog } from "../components/ActivityLog";
import { CreatorFilter } from "../components/CreatorFilter";
import { useAuth } from "../context/AuthContext";
import { api, errMsg } from "../lib/api";
import { exportPdf } from "../lib/pdf";

const EMPTY = { entries: [], total_deposit: 0, total_withdraw: 0, balance: 0 };

export default function Dashboard() {
  const { user, logout, isAdmin, can } = useAuth();
  const [sites, setSites] = useState([]);
  const [siteId, setSiteId] = useState(null);
  const [branches, setBranches] = useState([]);
  const [branchId, setBranchId] = useState("");
  const [range, setRangeState] = useState({ start: "", end: "" });
  const [data, setData] = useState(EMPTY);
  const [dialog, setDialog] = useState(null);
  const [entryType, setEntryType] = useState(null);
  const [editing, setEditing] = useState(null);
  const [view, setView] = useState(can("dashboard") ? "overview" : "site");
  const overview = view === "overview";
  const openSite = (id) => { setSiteId(id); setView("site"); };

  const site = sites.find((s) => s.id === siteId);
  const branchMap = useMemo(() => Object.fromEntries(branches.map((b) => [b.id, b.name])), [branches]);

  const loadSites = useCallback(async () => {
    const { data } = await api.get("/sites");
    setSites(data);
    setSiteId((cur) => (data.some((s) => s.id === cur) ? cur : data[0]?.id || null));
  }, []);

  const [search, setSearch] = useState("");
  const [debounced, setDebounced] = useState("");
  const [createdBy, setCreatedBy] = useState("");
  useEffect(() => { const t = setTimeout(() => setDebounced(search), 300); return () => clearTimeout(t); }, [search]);

  const loadEntries = useCallback(async () => {
    if (!siteId) return setData(EMPTY);
    const params = { branch_id: branchId || undefined, start: range.start || undefined, end: range.end || undefined, search: debounced || undefined, created_by: createdBy || undefined };
    const { data } = await api.get(`/sites/${siteId}/entries`, { params });
    setData(data);
  }, [siteId, branchId, range, debounced, createdBy]);

  useEffect(() => { loadSites().catch((e) => toast.error(errMsg(e))); }, [loadSites]);
  useEffect(() => {
    setBranchId("");
    setCreatedBy("");
    if (siteId) api.get(`/sites/${siteId}/branches`).then((r) => setBranches(r.data));
    else setBranches([]);
  }, [siteId]);
  useEffect(() => { loadEntries().catch((e) => toast.error(errMsg(e))); }, [loadEntries]);

  const run = async (fn, msg) => {
    try { await fn(); if (msg) toast.success(msg); } catch (e) { toast.error(errMsg(e)); }
  };

  const addSite = (name) => run(async () => {
    const { data } = await api.post("/sites", { name });
    setDialog(null); await loadSites(); openSite(data.id);
  }, "Site added");

  const delSite = (s) => window.confirm(`Delete site "${s.name}" with all its branches and entries?`) &&
    run(async () => { await api.delete(`/sites/${s.id}`); await loadSites(); }, "Site deleted");

  const addBranch = (name) => run(async () => {
    const { data } = await api.post(`/sites/${siteId}/branches`, { name });
    setDialog(null); setBranches((b) => [...b, data]); loadSites();
  }, "Branch added");

  const delBranch = (b) => window.confirm(`Delete branch "${b.name}" and its entries?`) &&
    run(async () => {
      await api.delete(`/branches/${b.id}`);
      setBranches((x) => x.filter((y) => y.id !== b.id));
      if (branchId === b.id) setBranchId(""); else loadEntries();
      loadSites();
    }, "Branch deleted");

  const saveEntry = (body) => run(async () => {
    if (editing) await api.put(`/entries/${editing.id}`, body);
    else await api.post(`/sites/${siteId}/entries`, body);
    closeEntry(); await loadEntries();
  }, editing ? "Entry updated" : "Entry saved");

  const closeEntry = () => { setEntryType(null); setEditing(null); };
  const editEntry = (e) => { setEditing(e); setEntryType(e.type); };

  const delEntry = (e) => window.confirm("Delete this entry?") &&
    run(async () => { await api.delete(`/entries/${e.id}`); await loadEntries(); }, "Entry deleted");

  const pdf = () => exportPdf({ site, branchName: branchId ? branchMap[branchId] : "All branches", start: range.start, end: range.end, data, branchMap });

  const openEntry = (t) => branches.length ? setEntryType(t) : toast.error("Add a branch first");

  return (
    <div className="min-h-screen flex flex-col lg:flex-row">
      <SitesSidebar sites={sites} activeId={siteId} onSelect={openSite} view={view} onOverview={() => setView("overview")} onStaff={() => setView("staff")} onActivity={() => setView("activity")} onAccount={() => setDialog("account")} onAdd={() => setDialog("site")} onDelete={delSite} user={user} onLogout={logout} />
      <main className="flex-1 p-4 sm:p-6 lg:p-10 space-y-6 min-w-0">
        {view === "activity" && isAdmin ? (
          <ActivityLog />
        ) : view === "staff" && isAdmin ? (
          <StaffManager sites={sites} />
        ) : overview ? (
          <Overview onOpenSite={openSite} refreshKey={sites} />
        ) : !site && !isAdmin ? (
          <div className="border border-dashed border-line2 bg-surface p-12 text-center" data-testid="no-site-assigned-state">
            <h2 className="font-display text-2xl font-bold">No books assigned yet</h2>
            <p className="text-muted-c text-sm mt-1">Ask your admin to give you access to a book.</p>
          </div>
        ) : !site ? (
          <div className="border border-dashed border-line2 bg-surface p-12 text-center" data-testid="no-site-state">
            <h2 className="font-display text-2xl font-bold">Create your first site</h2>
            <p className="text-muted-c text-sm mt-1">A site (book) holds branches and their entries.</p>
            <Button className="mt-4 rounded-none bg-brand hover:bg-brand/90 text-onbrand" onClick={() => setDialog("site")} data-testid="empty-add-site-button"><Plus className="h-4 w-4 mr-1" /> Add Site</Button>
          </div>
        ) : (
          <>
            <header className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 rise">
              <div>
                <p className="font-mono text-[11px] uppercase tracking-widest text-muted-c">Book / Site</p>
                <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight" data-testid="active-site-name">{site.name}</h1>
              </div>
              <div className="flex flex-wrap gap-2">
                {can("deposit") && <Button onClick={() => openEntry("deposit")} className="rounded-none bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="deposit-entry-button"><Plus className="h-4 w-4 mr-1" /> Deposit</Button>}
                {can("withdraw") && <Button onClick={() => openEntry("withdraw")} className="rounded-none bg-red-600 hover:bg-red-700 text-white" data-testid="withdraw-entry-button"><Minus className="h-4 w-4 mr-1" /> Withdraw</Button>}
                {can("export") && <><Button variant="outline" onClick={pdf} disabled={!data.entries.length} className="rounded-none border-brand text-brand bg-transparent hover:bg-brand/10 hover:text-brand/80" data-testid="export-pdf-button"><FileDown className="h-4 w-4 mr-1" /> Export PDF</Button>
                <Button variant="outline" onClick={() => exportEntriesExcel({ site, data, branchMap })} disabled={!data.entries.length} className="rounded-none border-brand text-brand bg-transparent hover:bg-brand/10 hover:text-brand/80" data-testid="export-excel-button"><FileSpreadsheet className="h-4 w-4 mr-1" /> Export Excel</Button></>}
                <Button variant="outline" onClick={logout} className="rounded-none lg:hidden" data-testid="mobile-logout-button"><LogOut className="h-4 w-4" /></Button>
              </div>
            </header>

            <section className="space-y-2">
              <p className="font-mono text-[11px] uppercase tracking-widest text-muted-c">Branches</p>
              <div className="flex flex-wrap gap-2" data-testid="branch-tabs">
                <button onClick={() => setBranchId("")} data-testid="branch-tab-all"
                  className={`px-3 py-1.5 text-sm border transition-colors ${!branchId ? "bg-brand text-onbrand border-brand" : "bg-surface border-line hover:border-muted-c"}`}>All branches</button>
                {branches.map((b) => (
                  <span key={b.id} className={`group inline-flex items-center border transition-colors ${branchId === b.id ? "bg-brand text-onbrand border-brand" : "bg-surface border-line hover:border-muted-c"}`}>
                    <button onClick={() => setBranchId(b.id)} className="pl-3 pr-1 py-1.5 text-sm" data-testid={`branch-tab-${b.id}`}>{b.name}</button>
                    {isAdmin && <button onClick={() => delBranch(b)} className="pr-2 pl-1 opacity-50 hover:opacity-100 hover:text-red-500" data-testid={`delete-branch-${b.id}`} aria-label="Delete branch"><X className="h-3.5 w-3.5" /></button>}
                  </span>
                ))}
                {isAdmin && <button onClick={() => setDialog("branch")} className="px-3 py-1.5 text-sm border border-dashed border-brand text-brand hover:bg-brand/10 transition-colors" data-testid="add-branch-button">+ Add Branch</button>}
              </div>
            </section>

            <SummaryCards data={data} />
            <Filters start={range.start} end={range.end} setRange={(start, end) => setRangeState({ start, end })} />
            <div className="flex flex-col sm:flex-row gap-3">
            <CreatorFilter creators={data.creators || []} value={createdBy} onChange={setCreatedBy} />
            <div className="relative w-full max-w-sm">
              <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-c" />
              <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search description, e.g. cement"
                className="rounded-none pl-9 pr-8 bg-canvas border-line2" data-testid="entry-search-input" />
              {search && <button onClick={() => setSearch("")} className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-c hover:text-ink" data-testid="entry-search-clear" aria-label="Clear search"><X className="h-4 w-4" /></button>}
            </div>
            </div>
            <EntriesTable entries={data.entries} branchMap={branchMap} onDelete={can("delete") && delEntry} onEdit={can("edit") && editEntry} />
            <BranchCompareChart entries={data.entries} branches={branchId ? branches.filter((b) => b.id === branchId) : branches} />
            <BranchComparison entries={data.entries} branches={branchId ? branches.filter((b) => b.id === branchId) : branches} />
          </>
        )}
      </main>

      <NameDialog open={dialog === "site"} onOpenChange={(o) => !o && setDialog(null)} title="Add Site (Book)" placeholder="e.g. Horizon Heights" onSubmit={addSite} testid="site" />
      <NameDialog open={dialog === "branch"} onOpenChange={(o) => !o && setDialog(null)} title={`Add Branch to ${site?.name || ""}`} placeholder="e.g. North Wing" onSubmit={addBranch} testid="branch" />
      <AccountDialog open={dialog === "account"} onOpenChange={(o) => !o && setDialog(null)} />
      <EntryDialog type={entryType} entry={editing} onClose={closeEntry} branches={branches} defaultBranch={branchId} onSubmit={saveEntry} />
    </div>
  );
}
